<template>
  <div class="s-datepicker-popup">
    <div class="s-datepicker-popup-content">
      <div class="s-datepicker-popup-header">
        <span class="s-datepicker-popup-prev-month" @click="prevMonth">
          <icon name="left" />
        </span>
        <span class="s-datepicker-popup-current-month">{{ currentMonth }}</span>
        <span class="s-datepicker-popup-next-month" @click="nextMonth">
          <icon name="right" />
        </span>
      </div>
      <div class="s-datepicker-popup-body">
        <div class="s-datepicker-popup-row">
          <span
            class="s-datepicker-popup-cell"
            v-for="(item, index) in weeksText"
            :key="index"
            :class="{ 's-datepicker-popup-weekday-weekend': isWeekdayWeekend(index) }"
            >{{ item?.fields?.abbr?.value }}</span
          >
        </div>
        <div class="s-datepicker-popup-row" v-for="(week, index) in weeks" :key="index">
          <span
            class="s-datepicker-popup-cell"
            v-for="(day, i) in week"
            :key="i"
            :class="{
              filled: day !== '',
              's-datepicker-popup-disabled': isNotChoose(day),
              's-datepicker-popup-select': isSelect(day),
              's-datepicker-popup-weekend': isWeekend(day, i),
            }"
            @click="selectDate(day)"
          >
            {{ day }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, onMounted, reactive, toRefs, watch } from 'vue';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    weeksText: {
      type: Array,
    },
    monthsText: {
      type: Array,
    },
    selected: {
      type: String,
    },
    startData: {
      type: [String, Number],
    },
    endData: {
      type: [String, Number],
    },
  },
  emits: ['change'],
  setup(props, ctx) {
    const state = reactive({
      year: 0,
      month: 0,
      day: 0,
      selectDay: '',
    });
    const computes = {
      currentMonth: computed(() => {
        const index = state.month - 1 > -1 ? state.month - 1 : 0;
        const month = props.monthsText?.[index]?.fields?.text?.value;
        return `${month} ${state.year}`;
      }),
      weeks: computed(() => {
        return methods.getWeeks(state.year, state.month);
      }),
    };
    const methods = {
      showPicker() {
        if (props.selected) {
          state.selectDay = props.selected;
          const dates = props.selected.split('-');
          state.day = dates[0];
          state.month = dates[1];
          state.year = dates[2];
        } else {
          const date = new Date();
          state.year = date.getFullYear();
          state.month = date.getMonth() + 1;
          state.day = date.getDate();
        }
      },
      prevYear() {
        state.year--;
      },
      nextYear() {
        state.year++;
      },
      prevMonth() {
        if (state.month === 1) {
          state.year--;
          state.month = 12;
        } else {
          state.month--;
        }
      },
      nextMonth() {
        if (state.month === 12) {
          state.year++;
          state.month = 1;
        } else {
          state.month++;
        }
      },
      getWeeks(year, month) {
        const weeks = [];
        const firstDay = new Date(year, month - 1, 1).getDay();
        const lastDay = new Date(year, month, 0).getDate();
        let week = [];
        for (let i = 0; i < firstDay; i++) {
          week.push('');
        }
        for (let i = 1; i <= lastDay; i++) {
          week.push(i);
          if ((firstDay + i) % 7 === 0) {
            weeks.push(week);
            week = [];
          }
        }
        if (week.length > 0) {
          let l = week.length;
          for (let i = 0; i < 7 - l; i++) {
            week.push('');
          }
          weeks.push(week);
        }
        return weeks;
      },
      selectDate(day) {
        if (day) {
          if (methods.isNotChoose(day)) return;
          state.selectDay = `${day}-${state.month}-${state.year}`;
          ctx.emit('change', state.selectDay);
        }
      },

      isNotChoose: (day) => {
        if (state.year && day) {
          let d = `${day}-${state.month}-${state.year}`;
          return methods.afterDates(d) || methods.beforeDates(d);
        }
        return false;
      },
      afterDates(date) {
        const [day, month, year] = date.split('-');
        const d = `${year}-${month}-${day}`.replace(/-/g, '/');
        if (props.startData) {
          if (props.startData < new Date(d).getTime()) {
            return false;
          }
          return true;
        }
        return false;
      },
      beforeDates(date) {
        const [day, month, year] = date.split('-');
        const d = `${year}-${month}-${day}`.replace(/-/g, '/');
        if (props.endData) {
          if (props.endData > new Date(d).getTime()) {
            return false;
          }
          return true;
        }
        return false;
      },
      isSelect(day) {
        return state.selectDay == `${day}-${state.month}-${state.year}`;
      },
      isWeekend(day, dayIndex) {
        return day && (dayIndex === 0 || dayIndex === 6);
      },
      isWeekdayWeekend(weekdayIndex) {
        return weekdayIndex === 0 || weekdayIndex === 6;
      },
    };
    watch(
      () => props.selected,
      (newValue) => {
        if (newValue) {
          methods.showPicker();
        }
      }
    );

    onMounted(async () => {
      methods.showPicker();
    });

    return {
      ...toRefs(state),
      ...computes,
      ...methods,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.s-datepicker-popup {
  height: 430px;
  color: $black;
  &-prev-month,
  &-next-month {
    cursor: pointer;
  }
  &-content {
    padding: 0 20px 30px;
  }
  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 18px;
    svg {
      width: 16px;
    }
  }
  &-row {
    display: flex;
    padding-bottom: 8px;
    justify-content: space-between;
  }
  &-cell {
    width: 44px;
    height: 44px;
    text-align: center;
    font-size: 16px;
    line-height: 44px;
    cursor: pointer;
  }
  &-disabled {
    color: rgb(230, 230, 230);
  }
  &-select {
    background-color: rgba(0, 0, 0, 1);
    border-radius: 50%;
    color: $white;
  }
  &-weekend {
    color: #828282;
  }
  &-weekend.s-datepicker-popup-disabled {
    color: rgb(230, 230, 230) !important;
  }
  &-weekday-weekend {
    color: #4964a2;
  }
  @include desktop {
    padding-top: 14px;
    height: 100%;
    position: absolute;
    z-index: 3;
    width: 100%;
  }
}
</style>
